import { useState, useRef } from 'react'
import { StepProps } from '../IAppModels'
import { Spinner } from 'react-bootstrap'

const Step4 = ({ data, updateData, onValidationChange }: StepProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles: File[] = Array.from(files);
    const errors: string[] = [];

    // Validate each file
    newFiles.forEach(file => {
      // Check file size (2MB = 2 * 1024 * 1024 bytes)
      if (file.size > 2 * 1024 * 1024) {
        errors.push(`${file.name} is too large. Maximum size is 2MB`);
      }

      // Check file type
      const fileType = file.name.split('.').pop()?.toLowerCase();
      if (!['docx', 'pdf'].includes(fileType || '')) {
        errors.push(`${file.name} is not a supported file type`);
      }
    });

    if (errors.length > 0) {
      setFileErrors(errors);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFiles(prevFiles => [...prevFiles, ...newFiles]);
    setFileErrors([]);

    // Update the form data
    updateData({
      appFiles: {
        ...data.appFiles,
        files: [...selectedFiles, ...newFiles],
      },
    });

    // Notify parent that files are valid (at least one file is uploaded)
    if (onValidationChange) {
      onValidationChange(true);
    }

    // Reset file input after successful upload
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Begin upload process simulation
    uploadFiles(newFiles);
  };

  const uploadFiles = async (filesToUpload: File[]) => {
    if (filesToUpload.length === 0) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    setSuccessMessage('');
    
    // Simulate file upload with progress
    // In a real implementation, you would use axios with onUploadProgress
    // similar to the Diagnosis.tsx implementation
    let progress = 0;
    const interval = setInterval(() => {
      progress += 5;
      setUploadProgress(progress);
      
      if (progress >= 100) {
        clearInterval(interval);
        setIsUploading(false);
        setSuccessMessage('Files uploaded successfully!');
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      }
    }, 200);
  };

  const removeFile = (index: number) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);

    // Update the form data
    updateData({
      appFiles: {
        ...data.appFiles,
        files: updatedFiles,
      },
    });

    // Reset file input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Notify parent - validation is false if no files remain
    if (onValidationChange) {
      onValidationChange(updatedFiles.length > 0);
    }
  };

  return (
    <>
      <div className='pb-5' data-kt-stepper-element='content'>
        <div className='w-100'>
          <div className='fv-row mb-10'>
            <label className='fs-5 fw-semibold mb-2 required'>Upload Documents</label>
            <div className='border-dashed p-4'>
              <div className='d-flex flex-column'>
                {/* Upload Area */}
                <div className='upload-area mb-3 p-4 border-dashed d-flex flex-column align-items-center justify-content-center' 
                     style={{ 
                       minHeight: '150px',
                       backgroundColor: '#f8f9fa',
                       cursor: 'pointer',
                       position: 'relative',
                       border: '2px dashed #dee2e6',
                       borderRadius: '8px'
                     }}>
                  <i className='fas fa-upload fs-2x mb-3'></i>
                  <span className='text-muted mb-2'>.docx, .pdf Maximum file size 2MB</span>
                  <input
                    ref={fileInputRef}
                    type='file'
                    className='form-control form-control-lg form-control-solid'
                    name='uploadDocuments'
                    onChange={handleFileChange}
                    accept='.docx,.pdf'
                    multiple
                    style={{
                      opacity: 0,
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer',
                      zIndex: 2
                    }}
                  />
                  <button 
                    className='btn btn-sm btn-light-primary'
                    style={{ zIndex: 1 }}
                  >
                    Choose Files
                  </button>
                </div>

                {/* Upload Progress Bar */}
                {isUploading && (
                  <div className="mb-3 p-4" style={{
                    background: 'white', 
                    padding: '16px', 
                    borderRadius: '10px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{
                      marginBottom: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#059BB6',
                        fontWeight: 500
                      }}>
                        <Spinner 
                          animation='border'
                          size='sm'
                          style={{
                            marginRight: '8px',
                            color: '#059BB6',
                            width: '16px',
                            height: '16px'
                          }}
                        />
                        Uploading files...
                      </span>
                      <span style={{
                        background: '#059BB6',
                        color: 'white',
                        borderRadius: '20px',
                        padding: '2px 10px',
                        fontSize: '14px',
                        fontWeight: 500
                      }}>
                        {uploadProgress}%
                      </span>
                    </div>
                    <div style={{
                      background: '#f0f0f0',
                      borderRadius: '6px',
                      height: '8px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${uploadProgress}%`,
                        height: '8px',
                        background: 'linear-gradient(90deg, #059BB6 0%, #05b6a6 100%)',
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }}/>
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {successMessage && (
                  <div style={{
                    marginBottom: '16px',
                    padding: '12px 16px',
                    background: '#d4edda',
                    color: '#155724',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 500,
                    boxShadow: '0 2px 6px rgba(21, 87, 36, 0.1)'
                  }}>
                    {successMessage}
                  </div>
                )}

                {/* File List with Scrollbar */}
                {selectedFiles.length > 0 && (
                  <div className='selected-files mt-4'>
                    <h6 className='fw-bold mb-3'>Selected Files:</h6>
                    <div style={{ 
                      maxHeight: '300px', 
                      overflowY: 'auto',
                      borderRadius: '8px',
                      border: '1px solid #eee',
                      padding: '10px',
                      // Custom Scrollbar Styling
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#dee2e6 #ffffff',
                    }}>
                      {selectedFiles.map((file, index) => (
                        <div 
                          key={index} 
                          className='d-flex align-items-center justify-content-between p-3 bg-light-primary rounded mb-2'
                          style={{ 
                            transition: 'all 0.3s ease',
                            borderBottom: index !== selectedFiles.length - 1 ? '1px solid #e9ecef' : 'none'
                          }}
                        >
                          <div className='d-flex align-items-center'>
                            <i className='fas fa-file-alt fs-2 text-primary me-3'></i>
                            <div>
                              <span className='fw-bold d-block' style={{ 
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {file.name}
                              </span>
                              <span className='text-muted fs-7'>
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </div>
                          </div>
                          <button
                            type='button'
                            className='btn btn-icon btn-sm btn-light-danger'
                            onClick={() => removeFile(index)}
                            style={{
                              width: '32px',
                              height: '32px',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <i className='fas fa-times'></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Error Messages */}
                {fileErrors.length > 0 && (
                  <div className='alert alert-danger mt-3'>
                    {fileErrors.map((error, index) => (
                      <div key={index} className='d-flex align-items-center'>
                        <i className='fas fa-exclamation-circle me-2'></i>
                        {error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          /* Custom scrollbar styles for Webkit browsers */
          .selected-files > div::-webkit-scrollbar {
            width: 6px;
          }

          .selected-files > div::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }

          .selected-files > div::-webkit-scrollbar-thumb {
            background: #dee2e6;
            border-radius: 3px;
          }

          .selected-files > div::-webkit-scrollbar-thumb:hover {
            background: #c1c9d0;
          }
        `}
      </style>
    </>
  )
}

export { Step4 }